import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import toast from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';

import {
  namespaces,
  useSharedTranslation,
} from 'us-react-components/dist/hooks/useSharedTranslation';

import { Button, ButtonType } from '../../components';
import { useDependencies } from '../../dependencies';
import { BackgroundLayout } from './components/BackgroundLayout';

export const Availability = observer((): JSX.Element => {
  const { t } = useSharedTranslation(namespaces.caregiver);
  const navigate = useNavigate();
  const { id: pathId } = useParams<{ id: string }>();
  const { stores } = useDependencies();
  const flow = stores.reviewFlow;
  const parentId = pathId ? Number(pathId) : undefined;

  const [hideAvailability, setHideAvailability] = useState<boolean | undefined>(
    flow.parent?.relationship?.blocked,
  );
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    document.title = 'UrbanSitter - Availability';
  }, []);

  useEffect(() => {
    if (!parentId) return;
    if (!flow.isValidForParent(parentId)) {
      navigate(`/parent/${parentId}/feedback`, { replace: true });
    }
  }, [parentId, flow, navigate]);

  const name = flow.parent?.firstName ?? '';
  const alreadyBlocked = !!flow.parent?.relationship?.blocked;
  const canSubmit = hideAvailability !== undefined && !submitting;

  const submit = async (): Promise<void> => {
    if (!canSubmit || !parentId) return;
    setSubmitting(true);
    try {
      if (hideAvailability && !alreadyBlocked) {
        await flow.blockParent();
      } else if (!hideAvailability && alreadyBlocked) {
        await flow.unblockParent();
      }
      await flow.saveReview();
      navigate(`/parent/${parentId}/feedback/success`);
    } catch {
      toast.error(t('pages.parentFeedback.availability.submitFailed'));
      setSubmitting(false);
    }
  };

  const options: Array<{
    value: boolean;
    id: string;
    labelKey: string;
    bodyKey: string;
  }> = [
    {
      value: true,
      id: 'hide-avail-yes',
      labelKey: 'pages.parentFeedback.availability.yesLabel',
      bodyKey: 'pages.parentFeedback.availability.yesBody',
    },
    {
      value: false,
      id: 'hide-avail-no',
      labelKey: 'pages.parentFeedback.availability.noLabel',
      bodyKey: 'pages.parentFeedback.availability.noBody',
    },
  ];

  return (
    <BackgroundLayout id='parent-review'>
      <h1 className='text-2xl font-semibold mb-2'>
        {t('pages.parentFeedback.availability.heading', { name })}
      </h1>
      <h2 className='text-base text-neutral-600 mb-6'>
        {t('pages.parentFeedback.availability.subheading', { name })}
      </h2>

      <div className='space-y-4 mb-6'>
        {options.map((opt) => {
          const selected = hideAvailability === opt.value;
          return (
            <label
              key={opt.id}
              htmlFor={opt.id}
              className={`block rounded border px-4 py-3 cursor-pointer us-card ${
                selected ? 'border-sapphire bg-sapphire-50' : 'border-neutral-200 hover:bg-neutral-50'
              }`}
            >
              <input
                id={opt.id}
                type='radio'
                name='hide-availability'
                checked={selected}
                onChange={(): void => setHideAvailability(opt.value)}
                className='sr-only'
              />
              <span className='block font-bold'>{t(opt.labelKey)}</span>
              <p className='mb-0 font-light text-sm'>{t(opt.bodyKey)}</p>
            </label>
          );
        })}
      </div>

      <div className='text-center mt-5'>
        <Button
          buttonType={ButtonType.primary}
          onClick={submit}
          disabled={!canSubmit}
        >
          {t('pages.parentFeedback.availability.submit')}
        </Button>
      </div>
    </BackgroundLayout>
  );
});
